import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Globe, Sun, Moon, MonitorSmartphone } from 'lucide-react';
import QRGenerator from './QRGenerator';
import MenuDropdown from './components/MenuDropdown';
import MenuItem from './components/MenuItem';
import GenQR from './genqr.png';
import updateMetadata from './lib/updateMetadata';

export const SUPPORTED_LANGUAGES = ['de', 'en', 'es', 'fr', 'it', 'jp', 'pl'];
const languages = { 'de': 'Deutsch', 'en': 'English', 'es': 'Español', 'fr': 'Français', 'it': 'Italiano', 'jp': '日本語', 'pl': 'Polski' };

function App({ defaultLanguage }) {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(['common']);
    //const { t: tq } = useTranslation(['qrGenerator']);
    const [language, setLanguage] = useState('en');
    const [theme, setTheme] = useState('auto');
    const currentDomain = window.location.hostname ?? 'genqr.pl';

    // Initialize theme from localStorage or system preference
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setTheme(savedTheme);
        } else {
            setTheme('auto');
        }
    }, []);

    // Initialize language based on URL path
    useEffect(() => {
        i18n.changeLanguage(defaultLanguage);
        setLanguage(defaultLanguage);
    }, [defaultLanguage, i18n]);

    // // Initialize language from localStorage or system preference
    // useEffect(() => {
    //     const savedLang = localStorage.getItem('i18nextLng');
    //     const isPLDomain = window.location.hostname.endsWith('.pl');
    //     const defaultLang = isPLDomain ? 'pl' : i18n.language;
    //     const currentLang = (savedLang ?? defaultLang).split('-')[0];
    //     const selectedLang = SUPPORTED_LANGUAGES.includes(currentLang) ? currentLang : 'en';
    //     i18n.changeLanguage(selectedLang);
    //     setLanguage(selectedLang);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);

    // Handle theme changes and updates
    useEffect(() => {
        const html = document.documentElement;

        if (theme === 'auto') {
            // Remove manual dark/light classes
            html.classList.remove('dark', 'light');

            // Add or remove dark class based on system preference
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                html.classList.add('dark');
            } else {
                html.classList.remove('dark');
            }

            // Listen for system theme changes
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = (e) => {
                if (e.matches) {
                    html.classList.add('dark');
                } else {
                    html.classList.remove('dark');
                }
            };

            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        } else {
            // Remove auto listener effects
            html.classList.remove('dark', 'light');

            // Add selected theme
            if (theme === 'dark') {
                html.classList.add('dark');
            }
        }

        // Save theme preference
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        updateMetadata(t, i18n.language);
    }, [t, i18n.language])

    const handleThemeChange = (newTheme) => {
        setTheme(newTheme);
    };

    const handleLanguageChange = (newLang) => {
        setLanguage(newLang);
        i18n.changeLanguage(newLang);
        localStorage.setItem('i18nextLng', newLang);
        navigate(`/${newLang}${window.location.pathname.substring(3)}`);
    };

    const LanguageMenuItem = ({ lang }) => {
        const label = lang.toUpperCase()
        return (<MenuItem onClick={() => handleLanguageChange(lang)} checked={language === lang}>
            <img src={`/${lang}.svg`} alt={label} className="w-4 h-4 mr-2" />
            {languages[lang]}
        </MenuItem>);
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <header className="bg-white dark:bg-gray-800 shadow-sm">
                <div className="max-w-2xl mx-auto pl-2 pr-2">
                    <div className="flex justify-between h-16">
                        {/* Logo */}
                        <div className="flex-shrink-0 flex items-center">
                            <img src={GenQR} alt="GenQR" />
                            <span className="text-3xl font-bold text-gray-700 dark:text-gray-200 pb-1.5 mx-4">
                                GenQR
                            </span>
                        </div>

                        {/* Navigation */}
                        <nav className="flex items-center space-x-4">
                            {/* About Link */}
                            <a
                                href="#about"
                                className="text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 px-3 py-2 text-sm font-medium"
                            >
                                {t('header.about')}
                            </a>

                            {/* Language Dropdown */}
                            <MenuDropdown title={<><Globe className="h-4 w-4 mr-1" />{languages[language]}</>}>
                                <LanguageMenuItem lang="de" />
                                <LanguageMenuItem lang="en" />
                                <LanguageMenuItem lang="es" />
                                <LanguageMenuItem lang="fr" />
                                <LanguageMenuItem lang="it" />
                                <LanguageMenuItem lang="jp" />
                                <LanguageMenuItem lang="pl" />
                            </MenuDropdown>

                            {/* Theme Dropdown */}
                            <MenuDropdown title={<>
                                {theme === 'light' && <Sun className="h-4 w-4 mr-1" />}
                                {theme === 'dark' && <Moon className="h-4 w-4 mr-1" />}
                                {theme === 'auto' && <MonitorSmartphone className="h-4 w-4 mr-1" />}
                                {t('header.theme')}
                            </>}>
                                <MenuItem onClick={() => handleThemeChange('auto')} checked={theme === 'auto'}>
                                    <MonitorSmartphone className="h-4 w-4 mr-2" />
                                    {t('header.themeOptions.auto')}
                                </MenuItem>
                                <MenuItem onClick={() => handleThemeChange('light')} checked={theme === 'light'}>
                                    <Sun className="h-4 w-4 mr-2" />
                                    {t('header.themeOptions.light')}
                                </MenuItem>
                                <MenuItem onClick={() => handleThemeChange('dark')} checked={theme === 'dark'}>
                                    <Moon className="h-4 w-4 mr-2" />
                                    {t('header.themeOptions.dark')}
                                </MenuItem>
                            </MenuDropdown>
                        </nav>
                    </div>
                </div>
            </header>
            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <QRGenerator />
            </main>
            {/* <main className="bg-white dark:bg-gray-800 shadow-sm">
                <a name="about" />
                <div className="max-w-6xl mx-auto mt-4 py-8 text-center text-sm text-gray-500">
                    <h1 className="text-xl uppercase">About</h1>
                    <h2 className="text-3xl pt-2 pb-4 font-semibold">The <span className="font-bold">Free QR Code Generator</span> for High Quality QR Codes</h2>
                    <p className="text-base">
                        GenQR powstał w odpowiedzi na potrzebę szybkiego generowania kodów QR w trybie on-line bez potrzeby zakładania kont i płacenia abomanentów, bez miliona wyskakujących popupów z reklamami, a także bez podmiany linków na jakieś dziwne domeny z przekierowaniem.
                        Zawsze i za darmo masz możliwość wygenerowania kodu QR w formacie PNG nadającym się do internetu i SVG, który można dowolnie i bezstratnie skalować do druku.
                    </p>
                </div>
            </main>
            <footer className="shadow-sm">
                <div className="max-w-2xl mx-auto mt-4 pb-4 text-center text-sm text-gray-500">
                    {tq('terms', { host: currentDomain })}
                </div>
            </footer> */}
        </div>
    );
}

export default App;
